/* global TextEncoder, TextDecoder, CompressionStream, DecompressionStream, btoa, atob */
/**
 * Where a kitchen goes when the tab closes.
 *
 * Until this file the plan lived in one Vue ref, and closing the tab, or the app going to
 * sleep on a phone, threw an afternoon's work away. Three things, all on the device:
 *
 *   the current kitchen  -- autosaved on every change, restored on open
 *   saved kitchens       -- named copies a person keeps and comes back to
 *   a share link         -- the whole design in the URL, pictures aside, for a partner or the
 *                           maker to open with nothing installed
 *
 * The plan is a plain object already; `serialize` copies out the fields that ARE the design
 * and nothing that is scene state (run.from/to, run.problem are recomputed by the scene).
 * IndexedDB via core/storage.js, because a plan with pictures on it is bigger than
 * localStorage likes. Pure functions on top, storage underneath, so the encoding is tested.
 */
import { records } from '../core/storage.js'
import { planSvg } from './plan-svg.js'
import { blockedSpans, extents, presetRuns } from './room.js'

export const CURRENT = 'kitchen:current'
const PREFIX = 'kitchen:'

const FIELDS = ['id', 'makerId', 'name', 'roomHeight', 'shape', 'walls', 'islands', 'openings', 'doorStyleId',
  'colourId', 'handleId', 'benchtopId', 'splashbackId', 'kickColourId', 'floorId', 'wallColourId',
  'rangehoodId', 'dishwasherId', 'ovenId', 'fridgeId', 'locks', 'approvedAt',
  // Which Original this design was drawn from, if any. An id and nothing else: the Original
  // is a separate record and stays that way, so a design costs one short string to carry and
  // the room as scanned is never duplicated into every design made from it.
  'originalId']

/** The finishes, as the fields serialize writes them. Everything ending in Id that is one. */
const FINISH_IDS = FIELDS.filter((f) => f.endsWith('Id') && f !== 'makerId' && f !== 'originalId')

/** The design and nothing else. `images: false` leaves the pictures behind, for a link. */
export function serialize(plan, opts = {}) {
  const out = {}
  for (const f of FIELDS) if (plan[f] !== undefined) out[f] = clone(plan[f])
  out.runs = (plan.runs ?? []).map((r) => ({
    id: r.id, wallId: r.wallId, kind: r.kind,
    // Only when it is not the whole wall: an absent span means "fill it", which is what
    // almost every run is, and writing one out for each of them doubles the link for nothing.
    ...(r.span ? { span: { from: Math.round(r.span.from), to: Math.round(r.span.to) } } : {}),
    items: (r.items ?? []).map((i) => {
      const it = { id: i.id, w: Math.round(i.w * 10) / 10 }
      if (i.specId) it.specId = i.specId
      if (i.filler) it.filler = true
      if (i.stretched) it.stretched = true
      if (i.gap) it.gap = true
      return it
    }),
  }))
  if (opts.images !== false && plan.images?.length) out.images = plan.images.map((i) => ({ ...i }))
  return out
}

/** A plan back from storage or a link, or null if it is not one. */
export function deserialize(obj) {
  if (!obj || typeof obj !== 'object') return null
  const walls = Array.isArray(obj.walls) ? obj.walls : null
  if (!walls || walls.length < 3) return null
  if (!walls.every((w) => w && typeof w.id === 'string' && [w.x1, w.y1, w.x2, w.y2].every(Number.isFinite))) return null
  if (!Array.isArray(obj.runs)) return null
  const plan = {}
  for (const f of FIELDS) if (obj[f] !== undefined) plan[f] = clone(obj[f])
  plan.walls = walls.map((w) => ({ id: w.id, x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2 }))
  plan.runs = obj.runs
    .filter((r) => r && typeof r.id === 'string' && typeof r.wallId === 'string')
    .map((r) => ({
      id: r.id, wallId: r.wallId, kind: r.kind === 'wall' ? 'wall' : 'base',
      ...(r.span && Number.isFinite(r.span.from) && Number.isFinite(r.span.to)
        ? { span: { from: r.span.from, to: r.span.to } } : {}),
      items: Array.isArray(r.items) ? r.items.filter((i) => i && Number.isFinite(i.w)).map((i) => ({ ...i })) : [],
    }))
  plan.locks = Array.isArray(obj.locks) ? obj.locks : []
  plan.islands = Array.isArray(obj.islands)
    ? obj.islands.filter((s) => s && typeof s.id === 'string'
        && [s.x1, s.y1, s.x2, s.y2].every(Number.isFinite))
      .map((s) => ({ id: s.id, x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
        overhang: Number.isFinite(s.overhang) ? s.overhang : 300,
        carries: ['sink', 'cooktop'].includes(s.carries) ? s.carries : null }))
    : []
  plan.openings = Array.isArray(obj.openings)
    ? obj.openings.filter((o) => o && typeof o.wallId === 'string' && ['door', 'window'].includes(o.kind)
        && [o.at, o.width].every(Number.isFinite) && o.width > 0)
      .map((o, i) => ({ id: o.id ?? `o-${i}`, wallId: o.wallId, kind: o.kind,
        at: o.at, width: o.width,
        sill: Number.isFinite(o.sill) ? o.sill : (o.kind === 'window' ? 900 : 0),
        height: Number.isFinite(o.height) ? o.height : (o.kind === 'window' ? 1200 : 2040) }))
    : []
  plan.images = Array.isArray(obj.images) ? obj.images.filter((i) => typeof i?.dataUrl === 'string') : []
  plan.roomHeight = Number.isFinite(plan.roomHeight) ? plan.roomHeight : 2400
  if (plan.shape === undefined) plan.shape = null
  return plan
}

const clone = (v) => (v === undefined ? v : JSON.parse(JSON.stringify(v)))

// ── the link ───────────────────────────────────────────────────────────────────────────
//
// Deflated and base64url'd, so a whole L-shaped kitchen is about 1.2KB of URL fragment. The
// fragment never reaches a server, which is the point: the link IS the kitchen. Browsers
// without CompressionStream (none current, but) get plain JSON under a different prefix.

/*
 * THE COMPACT FORM, which is what makes the link a link rather than a paragraph.
 *
 * Serialising the plan and deflating it gave a 679-character URL. Most of those characters
 * were things the other end already knows or can work out: every cabinet carried an id
 * ("r-back-base-1759482910233-7") that exists only to be unique within one session, every
 * run carried another, every finish was named in full ("shaker", "basalt"), and every wall
 * carried both its ends when the loop means one wall's end IS the next one's start.
 *
 * So the link carries INDICES into the maker's own lists, one point per corner, and no ids
 * at all -- they are regenerated on the way in, deterministically, and the locks that
 * referred to them are carried as positions instead. About 300 characters for a whole
 * kitchen, less than half of what it was.
 *
 * It needs the maker to decode, which is the trade: the link is only meaningful to the app
 * that made it. That was already true -- it is a kitchen from one maker's range.
 */
const SHAPES = ['single', 'galley', 'l', 'u']
const WALL_IDS = ['back', 'left', 'right', 'front']
const FINISH_FIELDS = [
  ['doorStyleId', 'doorStyles'], ['colourId', 'colours'], ['benchtopId', 'benchtops'],
  ['splashbackId', 'splashbacks'], ['handleId', 'handles'], ['kickColourId', 'colours'],
  // APPENDED, never inserted. An older link carries a shorter finishes array, and every
  // field past its end resolves to "the maker's first one" -- which is exactly right for a
  // link made before floors and wall colour existed. That is why this needs no new version.
  ['floorId', 'floors'], ['wallColourId', 'wallColours'],
  ['rangehoodId', 'rangehoods'], ['dishwasherId', 'dishwashers'], ['ovenId', 'ovens'],
  ['fridgeId', 'fridges'],
]

/** A wall id as a small int where it is one of the usual four, else the string itself. */
const wallCode = (id) => (WALL_IDS.indexOf(id) >= 0 ? WALL_IDS.indexOf(id) : id)
const wallName = (c) => (typeof c === 'number' ? WALL_IDS[c] ?? `w${c}` : c)

export function compact(plan, maker) {
  const specIndex = new Map(maker.cabinets.map((c, i) => [c.id, i]))
  const walls = plan.walls ?? []
  const runs = plan.runs ?? []
  const locks = []
  const runOut = runs.map((r, ri) => {
    const items = (r.items ?? []).map((it, ii) => {
      if ((plan.locks ?? []).some((l) => l.itemId === it.id)) locks.push([ri, ii])
      if (it.gap) return [-2, Math.round(it.w)]
      if (it.filler) return [-1, Math.round(it.w)]
      return [specIndex.get(it.specId) ?? -1, Math.round(it.w)]
    })
    return r.span
      ? [wallCode(r.wallId), r.kind === 'wall' ? 1 : 0, items, Math.round(r.span.from), Math.round(r.span.to)]
      : [wallCode(r.wallId), r.kind === 'wall' ? 1 : 0, items]
  })
  return [
    3,
    walls.map((w) => wallCode(w.id)),
    // one point per corner: wall i runs from vertex i to vertex i+1, round the loop
    walls.flatMap((w) => [Math.round(w.x1), Math.round(w.y1)]),
    runOut,
    FINISH_FIELDS.map(([field, list]) => (maker[list] ?? []).findIndex((o) => o.id === plan[field])),
    locks,
    SHAPES.indexOf(plan.shape),
    Math.round(plan.roomHeight ?? 2400),
    plan.name ?? '',
    // Version 2 adds this. Openings are the record that the gap in a run is a consequence
    // of, so a link that dropped them would open as a kitchen with a cabinet across the door.
    (plan.openings ?? []).map((o) => [wallCode(o.wallId), o.kind === 'window' ? 1 : 0,
      Math.round(o.at), Math.round(o.width), Math.round(o.sill ?? 0), Math.round(o.height ?? 2040)]),
    // Version 3 adds the islands. Their ids are carried verbatim because a run points at
    // one by id, and the two have to still agree at the other end.
    (plan.islands ?? []).map((s) => [s.id, Math.round(s.x1), Math.round(s.y1),
      Math.round(s.x2), Math.round(s.y2), Math.round(s.overhang ?? 0),
      s.carries === 'sink' ? 1 : s.carries === 'cooktop' ? 2 : 0]),
  ]
}

export function expand(c, maker) {
  // Every version still out there still opens. 1 has no openings, 2 no islands.
  if (!Array.isArray(c) || ![1, 2, 3].includes(c[0])) return null
  const [, wallCodes, coords, runs, finishes, locks, shapeIdx, roomHeight, name, openings, islands] = c
  if (!Array.isArray(wallCodes) || !Array.isArray(coords) || wallCodes.length < 3) return null
  if (coords.length !== wallCodes.length * 2) return null

  const pts = wallCodes.map((_, i) => ({ x: coords[i * 2], y: coords[i * 2 + 1] }))
  if (!pts.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) return null
  const walls = wallCodes.map((code, i) => {
    const a = pts[i], b = pts[(i + 1) % pts.length]
    return { id: wallName(code), x1: a.x, y1: a.y, x2: b.x, y2: b.y }
  })

  const plan = {
    makerId: maker.id,
    roomHeight: Number.isFinite(roomHeight) ? roomHeight : 2400,
    shape: SHAPES[shapeIdx] ?? null,
    walls,
    images: [],
    locks: [],
  }
  if (name) plan.name = name
  plan.islands = (islands ?? []).map(([id, x1, y1, x2, y2, overhang, carriesFlag]) => ({
    id, x1, y1, x2, y2, overhang,
    carries: carriesFlag === 1 ? 'sink' : carriesFlag === 2 ? 'cooktop' : null,
  }))
  plan.openings = (openings ?? []).map(([wc, kindFlag, at, width, sill, height], i) => ({
    id: `o-${i}`, wallId: wallName(wc), kind: kindFlag === 1 ? 'window' : 'door', at, width, sill, height,
  }))
  FINISH_FIELDS.forEach(([field, list], i) => {
    const opt = (maker[list] ?? [])[finishes?.[i] ?? -1]
    if (opt) plan[field] = opt.id
  })

  plan.runs = (runs ?? []).map((r, ri) => {
    const [wc, kindFlag, items, spanFrom, spanTo] = r
    const wallId = wallName(wc)
    const kind = kindFlag === 1 ? 'wall' : 'base'
    const id = `r-${wallId}-${kind}-${ri}`
    return {
      id,
      wallId,
      kind,
      ...(Number.isFinite(spanFrom) && Number.isFinite(spanTo) ? { span: { from: spanFrom, to: spanTo } } : {}),
      items: (items ?? []).map(([si, w], ii) => {
        const item = { id: `${id}-${ii}`, w }
        // The same shape the layout makes them in: `filler` and `gap` are what the code
        // branches on, but every one of them carries the matching specId too, and a round
        // trip that quietly dropped it would leave two kinds of filler in the world.
        if (si === -2) { item.gap = true; item.specId = 'gap' }
        else if (si === -1) { item.filler = true; item.specId = 'filler' }
        else {
          const spec = maker.cabinets[si]
          if (!spec) { item.filler = true; return item }
          item.specId = spec.id
          // Derived rather than carried: a cabinet is "made at" a size when it is not the
          // catalogue one, which the width already says.
          if (Math.abs(w - spec.w) > 0.5) item.stretched = true
        }
        return item
      }),
    }
  })

  plan.locks = (locks ?? []).map(([ri, ii]) => {
    const item = plan.runs[ri]?.items?.[ii]
    if (!item?.specId) return null
    return { itemId: item.id, role: maker.cabinets.find((c) => c.id === item.specId)?.role }
  }).filter(Boolean)

  return plan
}

export async function encodeShare(plan, maker) {
  // `c` when there is a maker to index against, `d` when there is not: an old link of
  // either kind still opens, which matters because a link is the one thing here that
  // outlives the version of the app that made it.
  const body = maker
    ? JSON.stringify(compact(plan, maker))
    : JSON.stringify(serialize(plan, { images: false }))
  const tag = maker ? 'c' : 'd'
  const bytes = new TextEncoder().encode(body)
  if (typeof CompressionStream === 'function') {
    try {
      const packed = await pump(bytes, new CompressionStream('deflate-raw'))
      return tag + '.' + b64url(packed)
    } catch { /* fall through to plain */ }
  }
  return 'j.' + b64url(bytes)
}

export async function decodeShare(code, maker) {
  if (typeof code !== 'string' || code.length < 3 || code[1] !== '.') return null
  const kind = code[0]
  if (!'cdj'.includes(kind)) return null
  try {
    const raw = unb64url(code.slice(2))
    const bytes = kind === 'j' ? raw : await pump(raw, new DecompressionStream('deflate-raw'))
    const parsed = JSON.parse(new TextDecoder().decode(bytes))
    if (kind === 'c') return maker ? expand(parsed, maker) : null
    return deserialize(parsed)
  } catch {
    return null
  }
}

/** The code in a URL hash, if there is one: `#k=...`. */
export function codeFromHash(hash) {
  const m = /(?:^|[#&])k=([A-Za-z0-9._-]+)/.exec(hash ?? '')
  return m ? m[1] : null
}

export async function shareUrl(plan, maker, base) {
  const code = await encodeShare(plan, maker)
  const b = base ?? (typeof location !== 'undefined' ? location.origin + location.pathname : '')
  return `${b}#k=${code}`
}

async function pump(bytes, stream) {
  const w = stream.writable.getWriter()
  // The write's promise is attached to, not awaited: on rubbish input the compressor fails
  // at BOTH ends, and an unhandled rejection from the writer took the process down before
  // the reader's rejection -- which is the one inside the caller's try -- could be caught.
  w.write(bytes).then(() => w.close()).catch(() => {})
  const out = []
  const r = stream.readable.getReader()
  for (;;) { const { value, done } = await r.read(); if (done) break; out.push(value) }
  const n = out.reduce((s, c) => s + c.length, 0)
  const all = new Uint8Array(n)
  let at = 0
  for (const c of out) { all.set(c, at); at += c.length }
  return all
}

function b64url(bytes) {
  let s = ''
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000))
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}
function unb64url(s) {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4))
  const out = new Uint8Array(b.length)
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i)
  return out
}

// ── the device ─────────────────────────────────────────────────────────────────────────
//
// Every call here swallows a storage failure and answers with nothing, because private
// browsing, a full disk and a browser with IndexedDB switched off all look the same to the
// person: the kitchen still works, it just does not come back tomorrow.

export async function saveCurrent(plan) {
  try { await records.put({ id: CURRENT, data: serialize(plan) }) } catch { /* not kept */ }
}
export async function loadCurrent() {
  try { const r = await records.get(CURRENT); return r ? deserialize(r.data) : null } catch { return null }
}

/** Keep a copy under a name. Returns its id, or null. */
export async function saveKitchen(plan, maker, name) {
  const id = PREFIX + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
  const thumb = safeThumb(plan, maker)
  const cabinets = plan.runs.reduce((n, r) => n + r.items.filter((i) => i.specId && !i.gap).length, 0)
  try {
    // `originalId` is on the record as well as inside `data` so the list can say what a
    // design came from, and count a room's designs, without opening every saved plan.
    await records.put({ id, name: (name || 'My kitchen').slice(0, 60), savedAt: Date.now(), thumb, cabinets,
      originalId: plan.originalId ?? null, data: serialize(plan) })
    return id
  } catch { return null }
}
export async function listKitchens() {
  try {
    const all = await records.all()
    return all
      .filter((r) => typeof r.id === 'string' && r.id.startsWith(PREFIX) && r.id !== CURRENT
        && !r.id.startsWith(ORIGINAL))
      .sort((a, b) => (b.savedAt ?? 0) - (a.savedAt ?? 0))
      .map((r) => ({ id: r.id, name: r.name, savedAt: r.savedAt, thumb: r.thumb, cabinets: r.cabinets ?? 0,
        originalId: r.originalId ?? r.data?.originalId ?? null }))
  } catch { return [] }
}
export async function loadKitchen(id) {
  try { const r = await records.get(id); return r ? deserialize(r.data) : null } catch { return null }
}
export async function renameKitchen(id, name) {
  try {
    const r = await records.get(id)
    if (!r) return false
    await records.put({ ...r, name: (name || r.name).slice(0, 60) })
    return true
  } catch { return false }
}
export async function deleteKitchen(id) {
  try { await records.delete(id); return true } catch { return false }
}

function safeThumb(plan, maker) {
  try { return planSvg(plan, maker, { width: 180, height: 120, pad: 10 }) } catch { return '' }
}

// ── the Original ───────────────────────────────────────────────────────────────────────
//
// THE ROOM AS IT IS TODAY, AND NOBODY MAY EDIT IT.
//
// A LiDAR scan used to become "the kitchen": the walls, the doorways and every cupboard it
// found were dropped straight into the working plan, and the first drag, the first swap and
// the first "Design it for me" all wrote over the only record of what was actually there.
// A person could not answer "what have I got now?" an hour after scanning, and neither
// could their cabinet maker.
//
// So a scan makes TWO things. The Original is written once, at the moment of the scan, and
// is never written again -- it is what the room IS, and it is worth exactly as much as it
// is unedited. Designs are made FROM it, as many as anyone likes, and each carries an
// `originalId` back to the room it came from.
//
// What a design inherits is the ROOM: the walls, the doorways, the windows, the ceiling
// height, and optionally the island's footprint and the services as locks. What it does NOT
// inherit is the cabinets, unless it is asked to. An air-conditioner on a wall today is not
// a reason a new kitchen cannot have cupboards there, and a generator handed the old layout
// is a generator with nothing left to decide.

const ORIGINAL = 'kitchen:original:'

/** `orig-x` or the whole record key, either way the key. */
const origKey = (id) => (String(id ?? '').startsWith(ORIGINAL) ? String(id) : ORIGINAL + String(id ?? ''))

/**
 * Keep the room exactly as the scan left it.
 *
 * Built by serialising the plan -- so every field name is the one `deserialize` reads, and
 * an Original opens as a plan with no special case anywhere -- and then adding what only a
 * scan knows: what it saw, what is plumbed or gassed in, and where the USDZ of the room
 * went. The finishes are ALSO gathered under `finishes` because that is what the record's
 * shape promises; they are the same values as the flat fields beside them.
 *
 * `modelPath` is a `file://` path on this device. It is stored so the scan can be looked at
 * again, and it is never put in a share link: it means nothing anywhere else.
 *
 * @param {object} plan   the plan as `fromScan` left it
 * @param {object} found  `fromScan`'s own result, for `services` and `saw`
 * @returns {object|null} the Original, or null if this browser would not keep it
 */
export async function saveOriginal(plan, found, { name, modelPath } = {}) {
  const id = 'orig-' + Date.now().toString(36)
  const body = serialize(plan)
  // An Original is the room, not a design of it: it is nobody's descendant.
  delete body.originalId
  const original = {
    ...body,
    id,
    name: (name || 'As scanned').slice(0, 60),
    scannedAt: new Date().toISOString(),
    makerId: plan.makerId ?? null,
    finishes: Object.fromEntries(FINISH_IDS.filter((f) => body[f] !== undefined).map((f) => [f, body[f]])),
    services: (found?.services ?? []).map((s) => ({ id: s.id, role: s.role, wallId: s.wallId })),
    saw: clone(found?.saw ?? null),
    modelPath: modelPath ?? null,
    images: (plan.images ?? []).map((i) => ({ ...i })),
  }
  try {
    await records.put({
      id: origKey(id),
      name: original.name,
      scannedAt: original.scannedAt,
      // `savedAt` as well, because every other record in here sorts by it.
      savedAt: Date.now(),
      data: original,
    })
    return original
  } catch { return null }
}

/** One Original, by its own id or by its record key. Null if it is not there. */
export async function loadOriginal(id) {
  if (!id) return null
  try { const r = await records.get(origKey(id)); return r?.data ?? null } catch { return null }
}

/** Every Original on this device, newest first. */
export async function listOriginals() {
  try {
    const all = await records.all()
    return all
      .filter((r) => typeof r.id === 'string' && r.id.startsWith(ORIGINAL) && r.data)
      .sort((a, b) => (b.savedAt ?? 0) - (a.savedAt ?? 0))
      .map((r) => r.data)
  } catch { return [] }
}

/**
 * Throw a scan away.
 *
 * Only ever from the Kitchens sheet, behind a second confirmation, and only when no saved
 * design still points at it -- a design whose Original is gone can still be worked on, but
 * nobody can ever see the room it was drawn for again, and that is not a thing to lose to a
 * mis-tap. The refusal is the caller's to make; this is the delete.
 */
export async function deleteOriginal(id) {
  try { await records.delete(origKey(id)); return true } catch { return false }
}

/**
 * A new design for a room that has already been measured.
 *
 * The Original goes in, a plan comes out, and the Original is not touched -- everything is
 * cloned on the way in, so a caller may do what it likes to the plan it gets back.
 *
 *   keepLayout    true keeps the cabinets that are there today, as a starting point.
 *                 False is the usual one: the room, and nothing standing in it.
 *   lockServices  [{ id, role }] -- items from the Original's runs whose plumbing or gas
 *                 point is not moving. See below; this is the interesting part.
 *   keepIsland    the island's footprint comes across, or it does not (and its run with it).
 *   onlyWalls     the segments the person picked on the "which walls are the kitchen?"
 *                 step, island included. See below. Left out, nothing changes.
 *
 * WHICH WALLS THE KITCHEN IS ALLOWED ON.
 *
 * A scan of an open-plan floor measures the lounge and the dining room too, and every wall
 * of it is real. Without `onlyWalls` this puts a run on the shape's preset walls plus
 * wherever the Original had one -- which for the owner's twenty-wall scan is a kitchen
 * spread across the whole floor with a vast empty middle.
 *
 * With it, the walls the person ticked ARE the answer and nothing else is consulted: each
 * gets a base run and a row above it, exactly as tapping a wall in the plan does. The
 * preset is not added to it -- a preset is a guess about a four-wall room and this is not
 * one -- and the Original's own runs are not either, because a run on a wall they have just
 * unticked is the thing they untick it to be rid of. The walls left out keep their
 * doorways, their windows and their geometry: they are still the room.
 *
 * WHAT A LOCK MEANS WHEN THERE IS NOTHING TO LOCK.
 *
 * `design.js` fixes a cabinet in place by ITEM ID: `lockedPositions` walks the plan's runs
 * looking for the locked id, and where it finds one it hands the generator a fixed point at
 * that position. With `keepLayout` false there are no items at all, so a lock would name an
 * id that exists nowhere and be silently dropped -- and the sink would move, which is the
 * plumber's bill, not ours.
 *
 * So a lock KEEPS ITS ITEM. When keepLayout is false the design starts with exactly the
 * locked service cabinets and nothing else, each at the millimetre it stands at today, and
 * every other cabinet in the room is dropped. Position is kept by a `gap` item in front of
 * each one, because a run's positions are implied by the widths before them and there is no
 * other way to say "this one starts 1830 along" -- the gap is spacing, not cabinetry, and
 * the generator fills around both exactly as it fills around a doorway.
 *
 * The millimetres are ABSOLUTE along the wall, not offsets within a run: the design's runs
 * are fresh and fill their walls, where the Original's may have been shortened to whatever
 * the scan measured, so a raw offset would put the sink somewhere it never was.
 */
export function designFrom(original, maker, { keepLayout = false, lockServices = [], keepIsland = true,
  onlyWalls = null } = {}) {
  // Everything, once, at the door. Nothing below can reach the caller's Original.
  const src = clone(original) ?? {}
  const chosen = onlyWalls ? new Set(onlyWalls) : null
  const lim = maker.limits
  const stamp = Date.now().toString(36)
  let seq = 0
  const runId = (wallId, kind) => `r-${wallId}-${kind}-${stamp}-${seq++}`

  const finishes = src.finishes ?? src
  const plan = {
    makerId: maker.id,
    originalId: src.id ?? null,
    roomHeight: Number.isFinite(src.roomHeight) ? src.roomHeight : 2400,
    shape: src.shape ?? null,
    walls: (src.walls ?? []).map((w) => ({ ...w })),
    openings: (src.openings ?? []).map((o) => ({ ...o })),
    islands: keepIsland ? (src.islands ?? []).map((s) => ({ ...s })) : [],
    images: [],
    locks: [],
    runs: [],
  }
  for (const f of FINISH_IDS) if (finishes?.[f] !== undefined) plan[f] = finishes[f]

  // An island that is not coming takes its run with it: a run on a segment that is not in
  // the room is a run the scene cannot place and `extents` answers with zero.
  const kept = new Set(plan.islands.map((s) => s.id))
  const dropped = new Set((src.islands ?? []).map((s) => s.id).filter((id) => !kept.has(id)))
  // And a wall they have unticked takes its run with it, for the same reason: a run that
  // reaches the design is a run the scene fills.
  const srcRuns = (src.runs ?? []).filter((r) => !dropped.has(r.wallId))
    .filter((r) => !chosen || chosen.has(r.wallId))

  if (keepLayout) {
    /*
     * The layout as found, with every id made fresh.
     *
     * Two designs from one room must never share an item id: they are separate plans, but
     * they are also separate LOCKS, separate undo histories and separate share links, and an
     * id that means one cabinet here and another there is the kind of bug that only shows up
     * once somebody has done a day's work.
     */
    const renamed = new Map()
    plan.runs = srcRuns.map((r) => {
      const id = runId(r.wallId, r.kind)
      return {
        id, wallId: r.wallId, kind: r.kind,
        ...(r.span ? { span: { ...r.span } } : {}),
        items: (r.items ?? []).map((it, i) => {
          const item = { ...it, id: `${id}-${i}` }
          renamed.set(it.id, item.id)
          return item
        }),
      }
    })
    plan.locks = (lockServices ?? [])
      .map((l) => ({ itemId: renamed.get(l.id), role: l.role }))
      .filter((l) => l.itemId)
  } else {
    /*
     * A room with nothing in it, which is what "Design it for me" was built for.
     *
     * The runs are the preset for the room's shape, the way a scan sets them up today, PLUS
     * a run wherever the Original had one: the scan is the only evidence about which walls
     * in this room actually carry cabinets, and a wall the preset does not cover is a wall a
     * locked sink could be standing against.
     */
    const wallIds = new Set(plan.walls.map((w) => w.id))
    // The picked walls ARE the answer where there is one: base and overheads on each, which
    // is what a tap on a wall in the plan gives. Otherwise the preset for the shape, plus
    // wherever the Original had a run -- see the note above the signature.
    const wanted = chosen
      ? plan.walls.filter((w) => chosen.has(w.id)).flatMap((w) => [[w.id, 'base'], [w.id, 'wall']])
      : presetRuns(plan.shape).filter(([wallId]) => wallIds.has(wallId))
    if (!chosen) {
      for (const r of srcRuns) {
        if (!wanted.some(([w, k]) => w === r.wallId && k === r.kind)) wanted.push([r.wallId, r.kind])
      }
    }
    // An island with no run of its own is an island nothing can be put on.
    for (const s of plan.islands) if (!wanted.some(([w]) => w === s.id)) wanted.push([s.id, 'base'])
    plan.runs = wanted.map(([wallId, kind]) => ({ id: runId(wallId, kind), wallId, kind, items: [] }))

    // Where the room's own runs sit, and where the new ones do. Both are needed to turn
    // "1200mm along the Original's back run" into "1500mm along the new one".
    const before = extents({ walls: plan.walls, islands: src.islands ?? [], openings: plan.openings,
      runs: src.runs ?? [] }, lim)
    const after = extents(plan, lim)

    const perRun = new Map()
    for (const want of lockServices ?? []) {
      const found = findItem(src.runs ?? [], want.id)
      if (!found) continue
      const to = plan.runs.find((r) => r.wallId === found.run.wallId && r.kind === found.run.kind)
      if (!to) continue
      const at = (before[found.run.id]?.from ?? 0) + found.offset
      const e = after[to.id] ?? { from: 0, to: 0 }
      if (!perRun.has(to)) perRun.set(to, [])
      perRun.get(to).push({
        start: at - e.from,
        item: { ...found.item, id: found.item.id },
        role: want.role ?? maker.cabinets.find((c) => c.id === found.item.specId)?.role,
      })
    }

    for (const [run, list] of perRun) {
      const e = after[run.id] ?? { from: 0, to: 0 }
      const space = Math.max(0, e.to - e.from)

      /*
       * AND THE DOORWAYS, WHICH A LOCK MAY NOT BE STOOD IN.
       *
       * This is the bug the owner hit as "Nothing fits with the cabinets you have locked
       * where they are. (w2 wall: adds up to 7283 on a 6433 leg.)"
       *
       * A lock was placed here at the millimetre it stands at today and nothing here knew
       * about the openings. `design.js` then puts a fixed `gap` the width of every mid-wall
       * doorway into the SAME leg -- correctly; a doorway is not ours to move -- so a lock
       * that overlapped one produced two fixed things claiming the same stretch of wall.
       * `fillLeg` lays both out at full width, the leg adds up to one doorway too much, and
       * EVERY variant is rejected for it: three tastes by two dishwasher sides by two
       * towers, all of them, so the sheet said nothing fits and the room was left empty.
       *
       * A scan is what produces the overlap: `readOpenings` reads a wide cased opening off
       * a wall it did not quite line up with, and a benchtop measured past it puts a
       * cupboard in the doorway. Neither reading is exact and the doorway is the better of
       * the two -- so the lock steps past it, by the least it can, and stays locked.
       */
      const holes = blockedSpans(plan, run.wallId, run.kind, lim)
        .map((o) => ({ from: o.from - e.from, to: o.to - e.from }))
        .filter((o) => o.to > 0 && o.from < space)

      const items = []
      let at = 0
      for (const k of list.sort((a, b) => a.start - b.start)) {
        // Clamped into the run rather than refused: a doorway or a corner may have eaten
        // the last few millimetres in front of where the sink stands, and a sink 40mm from
        // where it is today is right where a lock is trying to keep it.
        let start = Math.min(Math.max(k.start, at), space - k.item.w)
        // Past any doorway it lands in. The holes are in order, so one pass settles it.
        for (const o of holes) if (start < o.to && start + k.item.w > o.from) start = o.to
        if (start < at || start + k.item.w > space + 0.5) continue
        const gap = Math.round((start - at) * 10) / 10
        if (gap > 0.5) items.push({ id: `${run.id}-gap-${items.length}`, w: gap, gap: true, specId: 'gap' })
        items.push(k.item)
        plan.locks.push({ itemId: k.item.id, role: k.role })
        at = start + k.item.w
      }
      run.items = items
    }
  }

  return plan
}

/** One item, wherever it is: its run and how many millimetres of run come before it. */
function findItem(runs, itemId) {
  for (const run of runs) {
    let offset = 0
    for (const item of run.items ?? []) {
      if (item.id === itemId) return { run, item, offset }
      offset += item.w
    }
  }
  return null
}
